import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useGiftForm } from '../GiftFormContext';
import { useSelector } from 'react-redux';
import Customize from './components/Customize';
import AmountSelector from './components/AmountSelector';
import ShippingAddress from './components/ShippingAddress';
import SenderInfo from './components/SenderInfo';
import Confirmation from './components/Confirmation';
import TokenSelector from './components/TokenSelector';
import { selectPrivyToken } from '../../redux/slices/auth';

// Define components at module level - STABLE REFERENCES
const STEP_COMPONENTS = {
  TokenSelector,
  Customize,
  AmountSelector,
  SenderInfo,
  ShippingAddress,
  Confirmation,
};

const buildSteps = (delivery, isAuthenticated) => {
  const steps = [
    {
      component: STEP_COMPONENTS.TokenSelector,
      title: 'Select Token',
      buttonLabel: 'Continue',
    },
    {
      component: STEP_COMPONENTS.Customize,
      title: 'Customize Gift',
      buttonLabel: 'Select Amount',
    },
    {
      component: STEP_COMPONENTS.AmountSelector,
      title: 'Select Amount',
      buttonLabel: isAuthenticated
        ? delivery === 'mail'
          ? 'Shipping Details'
          : 'Checkout'
        : 'Continue',
    },
  ];

  // Only include SenderInfo step if user is NOT authenticated
  if (!isAuthenticated) {
    steps.push({
      component: STEP_COMPONENTS.SenderInfo,
      title: 'Sender Info',
      buttonLabel: delivery === 'mail' ? 'Shipping Details' : 'Checkout',
    });
  }

  if (delivery === 'mail') {
    steps.push({
      component: STEP_COMPONENTS.ShippingAddress,
      title: 'Shipping Details',
      buttonLabel: 'Checkout',
    });
  }

  steps.push({
    component: STEP_COMPONENTS.Confirmation,
    title: 'Checkout',
    buttonLabel: 'Continue',
  });

  return steps;
};

export const useGiftFlow = (initialStep = 0) => {
  const { sendingStatus, delivery, targetTokenAddress, network } = useGiftForm();
  const privyToken = useSelector(selectPrivyToken);
  const isAuthenticated = !!privyToken;

  // Lock in the initial authentication state to keep steps array stable
  // Use the current auth state on first render, not in useEffect
  const initialAuthStateRef = useRef(isAuthenticated);
  if (initialAuthStateRef.current === undefined) {
    initialAuthStateRef.current = isAuthenticated;
  }

  // Build steps array using locked initial auth state - stays stable throughout flow
  const steps = useMemo(
    () => buildSteps(delivery, initialAuthStateRef.current),
    [delivery],
  );

  const [currentStep, setCurrentStep] = useState(initialStep);

  // If token/network is missing and we're past step 0, redirect to token selection
  useEffect(() => {
    if (currentStep > 0 && (!targetTokenAddress || !network)) {
      setCurrentStep(0);
    }
  }, [targetTokenAddress, network, currentStep]);

  // Handle sending status change - only force to confirmation when starting to send
  useEffect(() => {
    if (sendingStatus === 'sending' || sendingStatus === 'success') {
      setCurrentStep(steps.length - 1);
    }
  }, [sendingStatus, steps.length]);

  const handleStepChange = useCallback(
    (step) => {
      const newStep = Math.max(0, Math.min(step, steps.length - 1));
      setCurrentStep(newStep);
    },
    [steps.length],
  );

  const onBack = useCallback(() => {
    // Don't allow going back if we're actively sending or have succeeded
    if (sendingStatus === 'sending' || sendingStatus === 'success') {
      return;
    }
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, [sendingStatus]);

  const currentStepConfig = steps[currentStep];
  const Component = currentStepConfig?.component || STEP_COMPONENTS.TokenSelector;
  const currentTitle = currentStepConfig?.title || '';
  const buttonLabel = currentStepConfig?.buttonLabel || 'Continue';

  const shouldShowBackButton =
    currentStep > 0 &&
    (sendingStatus === 'idle' || currentStep < steps.length - 1);

  return {
    currentStep,
    totalSteps: steps.length,
    currentTitle,
    Component,
    handleStepChange,
    onBack,
    shouldShowBackButton,
    buttonLabel,
  };
};
